
<script setup>

  import { ref, onMounted } from 'vue';

  import { ChevronLeftIcon, ChevronRightIcon, ArrowRightCircleIcon } from '@heroicons/vue/24/outline';

  import { cycleRef } from '../utils.js';

  import { letterScenario } from '../stores/activity-3.js';

  import SimpleDialog from '../dialogs/simple.vue';

  import scenarioPaths from '../images/activity-3-part-2-scenario-*.jpg';

  import backgroundPath from '../images/distressed-background.jpg';

  const infoDialog = ref( null );

  const scenarios = [ {

    id: 'tourist',
    
    title: 'You are a tourist',
    
    text: '...who is vacationing at Lewis Mountain. Write a letter describing your vacation.',

    alt: 'A group of black visitors--three women and one man--sitting on a grass lawn.'
  
  }, {
    
    id: 'church-group',
    
    title: 'You\'re with a church group',
    
    text: '...and you attended the annual picnic at Lewis. Write a letter to your sister who is attending school in New York telling her about the picnic.',

    alt: 'A black church group--one man, standing in a suit, facing the camera--on the park grounds.'

  }, {

    id: 'employee',
    
    title: 'You are an employee',
    
    text: '...and living at Lewis Mountain. Write a letter to your family describing your experience here.',

    alt: 'Two black female employees sitting in front of a tree.'

  } ].map( obj => ( { ...obj, path: scenarioPaths[ obj.id ] } ) );

  const scenario = cycleRef( scenarios.length );

  const backward = ref( true );

  onMounted( () => infoDialog.value.open() );

</script>

<template>

  <article>
  
    <div class="grid md:grid-cols-2 bg-fixed bg-cover bg-center" :style="{ backgroundImage: `url( ${ backgroundPath } )` }">    

      <div class="flex items-center justify-center min-h-view px-6 py-12">

        <div class="w-full max-w-xl">

          <div class="text-md font-bold uppercase">Step 1</div>

          <div class="lg:flex items-center gap-6 pb-6 border-b-2 border-black">

            <h3 class="pt-2 text-4xl font-heading uppercase">Select a Scenario</h3>

            <div class="flex gap-4 mt-3 lg:mt-0">

              <button type="button" v-for="number in scenarios.length" @click="scenario = number - 1"
              
                :class="[ 'flex items-center justify-center w-10 h-10 font-bold rounded-full border-2 border-black',
                
                  { 'text-white bg-black': number - 1 === scenario } ]">
                
                {{ number }}
              
              </button>

            </div>

          </div>

          <div class="grid mt-12 overflow-hidden">

            <Transition

              :enter-from-class="backward ? '-translate-x-full' : 'translate-x-full'"

              :leave-to-class="backward ? 'translate-x-full' : '-translate-x-full'"

              enter-active-class="transition-transform duration-300"

              leave-active-class="transition-transform duration-300">

              <div class="row-start-1 col-start-1 sm:flex items-center" :key="scenario">

                <figure class="md:hidden h-64 sm:h-32 mx-auto aspect-square rounded-full bg-cover bg-center"
          
                  :style="{ backgroundImage: `url( ${ scenarios[ scenario ].path } )` }" :aria-label="scenarios[ scenario ].alt" />

                <div class="mt-12 sm:mt-0 sm:ml-6 md:ml-0">

                  <h4 class="text-4xl md:text-5xl font-heading uppercase">{{ scenarios[ scenario ].title }}</h4>

                  <p class="mt-4">{{ scenarios[ scenario ].text }}</p>

                </div>

              </div>

            </Transition>

          </div>

          <div class="flex gap-4 mt-12">

            <button type="button" @click="backward = true; scenario --" class="flex items-center justify-center w-14 h-14 bg-amber-300 rounded-full">

              <ChevronLeftIcon class="w-10 h-10" />

            </button>

            <button type="button" @click="backward = false; scenario ++" class="flex items-center justify-center w-14 h-14 bg-amber-300 rounded-full">

              <ChevronRightIcon class="w-10 h-10" />

            </button>

          </div>

          <div class="flex justify-end mt-12">

            <RouterLink
            
              class="flex items-center gap-4 px-6 py-4 font-bold text-white bg-green-700 transition-colors pointer-events-auto
              
                hover:bg-green-800 focus-visible:bg-green-800 hover:underline focus-visible:underline"
            
              to="/activity/3/part/2/step/2"
              
              @click="letterScenario = scenarios[ scenario ].id">

              <span>Select</span>

              <ArrowRightCircleIcon class="w-8" />

            </RouterLink>

          </div>

        </div>

      </div>

      <div class="hidden md:grid overflow-hidden">

        <Transition

          :enter-from-class="backward ? '-translate-y-full' : 'translate-y-full'"

          :leave-to-class="backward ? 'translate-y-full' : '-translate-y-full'"

          enter-active-class="transition-transform duration-300"

          leave-active-class="transition-transform duration-300">

          <div class="row-start-1 col-start-1 relative overflow-hidden" :key="scenario">

            <figure class="absolute h-full aspect-square rounded-full lg:rounded-none lg:rounded-l-full bg-cover bg-center"
          
              :style="{ backgroundImage: `url( ${ scenarios[ scenario ].path } )` }" :aria-label="scenarios[ scenario ].alt" />

          </div>

        </Transition>

      </div>

      <SimpleDialog ref="infoDialog" title="Time to Write a Letter" class="max-w-xl">

        <p class="text-neutral-700">

          In Part 2 of this Activity, you will reflect on what you heard in Part 1 and write a letter to friends and family back home.
          
          You can choose a scenario and customize the look of your letter and be able to download it when you're done, to share with your teacher or class.

        </p>

      </SimpleDialog>

    </div>

  </article>

</template>
